/**
 * A custom element to truncate content.
 */
export default class AiTruncate extends HTMLElement {
  /**
   * Initial truncated value.
   * @private
   *
   * @type {Boolean}
   */
  #isTruncated = false;

  /**
   * Flag for invalid configuration.
   * @private
   *
   * @type {Boolean}
   */
  #canTruncate = true;

  /**
   * Observes the specified attributes.
   * @static
   *
   * @return {array} A list of attributes to observe.
   */
  static get observedAttributes() {
    return ['truncated'];
  }

  /**
   * Create the custom element.
   */
  constructor() {
    super();

    /**
     * Holds nodes to truncate.
     *
     * @type {Array}
     */
    this.itemsToHide = [];

    /**
     * The maximum number shown when truncated.
     *
     * Refers to lines of text or child nodes.
     *
     * @type {Number}
     */
    this.maxLength = 4;
	
	this.allItems = [];
	this.filteredItems = [];
	this.visibleCount = 0;
  }

  /**
   * Update truncated state.
   *
   * @param {Boolean} isTruncated Whether the content should be truncated.
   */
  set truncated(isTruncated) {
    this.#isTruncated = isTruncated;

    if (this.truncated) {
      this.buttonLabel.textContent = this.truncatedLabel;
    } else if (undefined !== this.expandedLabel) {
      this.buttonLabel.textContent = this.expandedLabel;
    }

    if (this.contentType === 'nodes') {
      this.button.setAttribute('expanded', this.truncated);
    }

    this.setAttribute('truncated', isTruncated);
  }

  /**
   * Get truncated state.
   *
   * @return {Boolean} Whether the content is currently truncated.
   */
  get truncated() {
    return this.#isTruncated;
  }

  /**
   * Handle changes to observed attributes.
   *
   * @param {String} name     The attribute name.
   * @param {String} oldValue The attribute's previous value.
   * @param {String} newValue The attribute's current value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (this.contentType === 'nodes' && name === 'truncated') {
      // Set the nodes' style attribute based on whether they should be hidden.
      this.itemsToHide.forEach((item) => {
        item.setAttribute('style', (newValue === 'true' ? 'display:none' : ''));
      });
    }
  }

  /**
   * Toggle truncated state.
   */
  toggleTruncated = () => {
	if(document.body.classList.contains('search') || document.body.classList.contains('search-results')) {
		if (this.visibleCount >= this.filteredItems.length) {
			this.visibleCount = this.maxLength;
			this.buttonLabel.textContent = this.truncatedLabel;
		} else {
			this.visibleCount += this.maxLength;
		}
		this.updateVisibleItems();
	} else {
		this.truncated = (!this.truncated);
	}
  }

   updateVisibleItems = () => {

	  const selected = this.filteredItems.filter(item =>
		item.querySelector('input')?.checked
	  )

	  const unselected = this.filteredItems.filter(item =>
		!item.querySelector('input')?.checked
	  )

	  const visibleUnselected = unselected.slice(0, this.visibleCount)

	  const visibleItems = [...selected, ...visibleUnselected]

	  this.allItems.forEach(item => {

		if (visibleItems.includes(item)) {
		  item.style.display = ''
		} else {
		  item.style.display = 'none'
		}

	  })

	}
  
   /**
    * Individual Search Filter 
    */
   setUpSearch = () => {

	  const input = this.querySelector('.ai-truncate__search')
	  if (!input) return

	  input.addEventListener('input', (e) => {

		const query = e.target.value.toLowerCase()

		this.filteredItems = this.allItems.filter(item =>
		  item.textContent.toLowerCase().includes(query)
		)

		this.visibleCount = this.maxLength

		this.updateVisibleItems()

	  })

	}
	
  /**
   * Additional setup required for truncating text that was originally in the
   * root of this element.
   */
  setUpTextTruncation = () => {
    const div = document.createElement('div');
    div.className = 'ai-truncate__contents';
    div.innerHTML = this.innerHTML;

    // Wrap inner contents in a div.
    try {
      this.replaceChildren(div);
    } catch (error) {
      console.log(error); // eslint-disable-line no-console

      /*
       * Prevent truncating text if we can't move it out of the root, since that
       * would hide the button along with the text overflow!
       */
      this.#canTruncate = false;
    }
  }

  /**
   * Get the button template and add it to this element.
   */
  setTruncateButton = () => {
    // Get the button template.
    const template = document.getElementById('hbg-truncate-button');
    const templateContent = template.content;

    // Append the button.
    try {
      this.appendChild(templateContent.cloneNode(true));
    } catch (error) {
      console.error(error); // eslint-disable-line no-console

      // Make sure we don't truncate if the button isn't present.
      this.#canTruncate = false;
    }

    if (this.#canTruncate) {
      // Get the button.
      this.button = this.querySelector('.ai-truncate__button');

      if (this.button !== null) {
        // Set the initial button label.
        this.buttonLabel = this.button.querySelector('.ai-truncate__button-label');
        this.buttonLabel.textContent = this.truncatedLabel;

        // Set the button style, using `link` as the default value.
        const buttonStyle = this.getAttribute('button-style') || 'link';

        if (['link', 'rounded'].includes(buttonStyle)) {
          this.button.classList.add(`ai-truncate__button--${buttonStyle}`);
        } else {
          // Apply the custom class value.
          this.button.classList.add(buttonStyle);
        }

        if (this.contentType === 'nodes') {
          this.button.setAttribute('expanded', this.truncated);
        }

        /*
         * "Hide" the button if we're truncating text, since CSS truncation
         * doesn't prevent the truncated content from being presented in-full
         * to assistive tech.
         */
        if (this.contentType === 'text') {
          this.button.setAttribute('aria-hidden', 'true');
          this.button.setAttribute('tabindex', '-1');
        }

        this.button.addEventListener('click', this.toggleTruncated);
      }
    }
  }

  /**
   * Called by the browser when the element is added to the page.
   */
  connectedCallback() {

	  // The `label` attribute is required.
	  if (!this.hasAttribute('truncated-label')) {
		console.error('<ai-truncate> requires the `truncated-label` attribute', this)
		this.#canTruncate = false
	  }

	  // Button labels
	  this.truncatedLabel = this.getAttribute('truncated-label')
	  if (this.hasAttribute('expanded-label')) {
		this.expandedLabel = this.getAttribute('expanded-label')
	  }

	  // Content type
	  this.contentType = this.getAttribute('content') || 'text'

	  // Max items
	  if (this.hasAttribute('max')) {
		this.maxLength = Number(this.getAttribute('max'))
	  }

	  /*
	  -------------------------
	  TEXT TRUNCATION (existing)
	  -------------------------
	  */
	  if (this.contentType === 'text') {

		if (!this.hasAttribute('selector')) {
		  this.setUpTextTruncation()
		}

		if (this.hasAttribute('max')) {
		  this.style = `--ai-truncate-lines: ${this.maxLength}`
		}

	  }

	  /*
	  -------------------------
	  NODE TRUNCATION (filters)
	  -------------------------
	  */
	  if (this.contentType === 'nodes') {

		const truncateRoot = (this.children.length === 1)
		  ? this.children.item(0)
		  : this

		// collect all filter labels
		this.allItems = Array.from(truncateRoot.querySelectorAll('label'))

		// filtered list (used for search)
		this.filteredItems = [...this.allItems]

		// pagination counter
		this.visibleCount = this.maxLength

		// listen to checkbox changes (single listener)
		this.addEventListener('change', (e) => {

		  if (e.target.type === 'checkbox') {
			this.updateVisibleItems()
		  }

		})

		// setup search
		this.setUpSearch()

		// initial render
		this.updateVisibleItems()

		// check if truncation is needed
		this.#canTruncate = this.allItems.length > this.maxLength

	  }

	  /*
	  -------------------------
	  BUTTON SETUP
	  -------------------------
	  */
	  if (this.#canTruncate) {

		this.setTruncateButton()

		this.truncated = (this.getAttribute('truncated') !== 'false')

	  }

	}

  /**
   * Called by the browser when the element is removed from the page.
   */
  disconnectedCallback() {
    this.removeAttribute('truncated');
    if (this?.button) {
      this.button.removeEventListener('click', this.toggleTruncated);
    }
  }
}
